
import React, { useState, useEffect } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { generateQRCode, formatQRCode } from '@/utils/qrCodeUtils';

interface QRCodeGeneratorProps {
  code: string;
  size?: number;
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({ code, size = 280 }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Enhanced QR generation with retry logic and pre-processing
  useEffect(() => {
    // Validate input
    if (!code) {
      setIsLoading(false);
      setError('No code provided');
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    // Use standardized formatQRCode utility for consistent format
    const normalizedCode = formatQRCode(code);
    
    // Debug log to verify code format
    console.log(`QRCodeGenerator processing code: ${code} → ${normalizedCode}`);
    
    // Start QR code generation
    const generateCode = async () => {
      try {
        const dataUrl = await generateQRCode(normalizedCode);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setQrCodeUrl(dataUrl);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to generate QR code:', err);
        
        // Implement retry logic (up to 3 attempts)
        if (retryCount < 3 && isMounted) {
          setRetryCount(prev => prev + 1);
          // Retry with exponential backoff
          setTimeout(generateCode, 300 * Math.pow(2, retryCount));
        } else if (isMounted) {
          setError('Failed to generate QR code');
          setIsLoading(false);
        }
      }
    };

    generateCode();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [code, retryCount]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Skeleton className="rounded-md" style={{ width: size, height: size }} />
      </div>
    );
  }

  if (error || !qrCodeUrl) {
    return (
      <div className="rounded-md bg-gray-100 flex items-center justify-center"
           style={{ width: size, height: size }}>
        <p className="text-sm text-gray-500 text-center px-4">{error || 'Could not generate QR code'}</p>
      </div>
    );
  }

  return (
    <div className="qr-code-container">
      <img 
        src={qrCodeUrl} 
        alt={`QR code for registration: ${code}`} 
        width={size} 
        height={size} 
        className="rounded-md"
        // Adding cache busting to ensure fresh QR code
        onError={() => setError('Failed to load QR code')}
      />
    </div>
  );
};

export default QRCodeGenerator;
